import { useFormikContext } from 'formik';

import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { Box } from '../../../common/ui/Box';
import { VesselSelect } from '../../../vessel/VesselSelect';
import type { DemandDefinitionState } from '../../types/DemandDefinitionState';
import type { DemandFleetCompany } from '../../types/DemandFleetCompany';
import { LeavingVesselHandling } from '../../types/LeavingVesselHandling';

import { LeavingVesselCard } from './LeavingVesselCard';

interface ExpectedFleetChangesStepProps {
    companies: Pick<DemandFleetCompany, 'id' | 'vessels'>[];
}

export const ExpectedFleetChangesStep = ({ companies }: ExpectedFleetChangesStepProps) => {
    const { values, setFieldValue, isSubmitting } = useFormikContext<DemandDefinitionState>();
    const vessels = companies.flatMap((company) => company.vessels);
    const customerIds = companies
        .filter((company) => !values.excludedCompanyIds.includes(company.id))
        .map((company) => company.id);

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Text variant="subtitle1">{translate('demandDefinition.vesselsLeavingTheFleet')}</Text>
            <VesselSelect
                label={translate('global.coreData.vessel')}
                placeholder={translate('demandDefinition.selectVesselToRemove') + Characters.HORIZONTAL_ELLIPSIS}
                customerIds={customerIds}
                vesselId={null}
                customOptionsFilter={(options) =>
                    options.filter((option) => !values.leavingVessels.some((entry) => entry.vesselId === option.value))
                }
                disabled={isSubmitting}
                onChange={(vessel) => {
                    if (!vessel || values.leavingVessels.some((entry) => entry.vesselId === vessel.id)) {
                        return;
                    }
                    setFieldValue('leavingVessels', [
                        ...values.leavingVessels,
                        {
                            vesselId: vessel.id,
                            plannedLeaving: null,
                            handling: LeavingVesselHandling.ENTIRELY,
                        },
                    ]);
                }}
            />
            {values.leavingVessels.map((entry) => (
                <LeavingVesselCard
                    key={entry.vesselId}
                    entry={entry}
                    vessel={vessels.find((vessel) => vessel.id === entry.vesselId)}
                />
            ))}
        </Box>
    );
};
